// src/scenes/support/SupportPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Snackbar,
    Alert,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    Switch,
    FormControlLabel,
    CircularProgress,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Fade,
    Zoom,
} from "@mui/material";
import { styled, alpha, lighten, darken } from "@mui/material/styles";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CommentIcon from "@mui/icons-material/Comment";
import SendIcon from "@mui/icons-material/Send";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import DescriptionIcon from "@mui/icons-material/Description";
import { tokens } from "../../theme";

const API_BASE = "https://figazuresql.swancreate.com/api/swa";
const MEDIA_BASE = "https://media.swancreate.com";
const LOGO_URL = "https://figk-12.com/static/media/fig_CROP.cb0d7cf4314c63ba05ee.png";
const API_TICKETS = `${API_BASE}/SUPPORT_Tickets`;
const API_COMMENTS = `${API_BASE}/SUPPORT_TicketComments`;
const API_ATTACHMENTS = `${API_BASE}/SUPPORT_Attachments`;
const MAILGUN_SEND = "https://mailgun.swancreate.com/send";
const SUPPORT_LINK = `${window.location.origin}/support/supportpage`;

const PageWrap = styled(Box)(({ theme }) => ({
    padding: 16,
    minHeight: "100vh",
    background: theme.palette.mode === "dark"
        ? `radial-gradient(ellipse at top, ${alpha(theme.palette.primary.dark, 0.15)}, transparent), radial-gradient(ellipse at bottom, ${alpha(theme.palette.secondary.dark, 0.15)}, transparent)`
        : `radial-gradient(ellipse at top, ${alpha(theme.palette.primary.light, 0.08)}, transparent), radial-gradient(ellipse at bottom, ${alpha(theme.palette.secondary.light, 0.08)}, transparent)`,
    [theme.breakpoints.up("md")]: { padding: 24 },
}));

const Hero = styled(Paper)(({ theme }) => ({
    borderRadius: 20,
    padding: 24,
    marginBottom: 24,
    background: theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.4)} 0%, ${alpha(theme.palette.secondary.dark, 0.4)} 100%)`
        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
    boxShadow: theme.palette.mode === "dark"
        ? `0 0 0 1px ${alpha(theme.palette.divider, 0.6)}, 0 20px 40px ${alpha(theme.palette.common.black, 0.5)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}`
        : `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.8)}`,
    position: "relative",
    overflow: "hidden",
    backdropFilter: "blur(20px)",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "100%",
        background: theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.3)}, ${alpha(theme.palette.secondary.light, 0.3)})`,
        opacity: 0.5,
        pointerEvents: "none",
    },
}));

const Card = styled(Paper)(({ theme }) => ({
    borderRadius: 16,
    padding: 20,
    background: theme.palette.mode === "dark"
        ? alpha(theme.palette.background.paper, 0.7)
        : alpha(theme.palette.background.paper, 0.95),
    boxShadow: theme.palette.mode === "dark"
        ? `0 0 0 1px ${alpha(theme.palette.divider, 0.5)}, 0 12px 32px ${alpha(theme.palette.common.black, 0.4)}`
        : `0 4px 24px ${alpha(theme.palette.common.black, 0.08)}`,
    backdropFilter: "blur(10px)",
    transition: "transform 200ms ease, box-shadow 200ms ease",
    "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: theme.palette.mode === "dark"
            ? `0 0 0 1px ${alpha(theme.palette.divider, 0.6)}, 0 16px 40px ${alpha(theme.palette.common.black, 0.5)}`
            : `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
    },
}));

const SolidButton = styled(Button)(({ theme }) => ({
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 12,
    padding: "10px 20px",
    boxShadow: theme.palette.mode === "dark"
        ? `0 4px 12px ${alpha(theme.palette.common.black, 0.4)}`
        : `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}`,
    transition: "all 200ms ease",
    "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: theme.palette.mode === "dark"
            ? `0 8px 20px ${alpha(theme.palette.common.black, 0.5)}`
            : `0 4px 16px ${alpha(theme.palette.common.black, 0.2)}`,
    },
    "&:active": {
        transform: "translateY(0px)",
    },
}));

const SolidIconButton = styled(IconButton)(({ theme }) => ({
    borderRadius: 10,
    padding: 8,
    transition: "all 200ms ease",
    "&.resolve": {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.getContrastText(theme.palette.success.main),
        boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.4)}`,
        "&:hover": {
            backgroundColor: darken(theme.palette.success.main, 0.1),
            transform: "translateY(-2px)",
            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.5)}`,
        },
    },
    "&.delete": {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.getContrastText(theme.palette.error.main),
        boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.4)}`,
        "&:hover": {
            backgroundColor: darken(theme.palette.error.main, 0.1),
            transform: "translateY(-2px)",
            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.5)}`,
        },
    },
}));

const AttachmentPill = styled(Chip)(({ theme }) => ({
    borderRadius: 10,
    fontWeight: 600,
    "& .MuiChip-icon": { color: theme.palette.secondary.main },
}));

const SectionTitle = ({ children, icon }) => {
    const theme = useTheme();
    return (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            {icon}
            <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                {children}
            </Typography>
        </Stack>
    );
};

const sanitizeFilename = (s) => (s || "").toString().replace(/[^\w.-]+/g, "_").slice(0, 160);

const escapeHtml = (text) => {
    const map = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" };
    return (text || "").toString().replace(/[&<>"']/g, (m) => map[m]);
};

const vib = (pattern = 16) => {
    try {
        if (navigator?.vibrate) navigator.vibrate(pattern);
    } catch { }
};

async function downloadWithName(url, filename) {
    const resp = await axios.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
}

async function fetchByTicketId(url, ticketId) {
    try {
        const { data } = await axios.get(`${url}?TicketId=${encodeURIComponent(ticketId)}`);
        return (Array.isArray(data) ? data : []).filter((x) => (x.TicketId || x.ticketId || x.ticketid) === ticketId);
    } catch { }
    try {
        const { data } = await axios.get(`${url}?ticketid=${encodeURIComponent(ticketId)}`);
        return (Array.isArray(data) ? data : []).filter((x) => (x.TicketId || x.ticketId || x.ticketid) === ticketId);
    } catch { }
    const { data } = await axios.get(url);
    return (Array.isArray(data) ? data : []).filter((x) => (x.TicketId || x.ticketId || x.ticketid) === ticketId);
}

function buildEmailHTML({ title, TicketNumber, subject, body, fromLine, priority, extra, ctaLabel = "Open Support" }) {
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeHtml(title)}</title>
</head>
<body style="margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;background:#f5f7fb;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f5f7fb;padding:32px 16px;">
        <tr><td align="center">
            <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.1);">
                <tr>
                    <td align="center" style="padding:32px 24px;background:linear-gradient(135deg, rgba(37,99,235,0.1) 0%, rgba(147,51,234,0.1) 100%);border-bottom:2px solid rgba(37,99,235,0.15);">
                        <img src="${LOGO_URL}" alt="FIG" height="48" style="display:block;margin:0 auto 12px auto;border-radius:6px;">
                        <div style="font-size:24px;font-weight:800;color:#1e293b;letter-spacing:-0.5px;margin-bottom:4px;">${escapeHtml(title)}</div>
                        <div style="font-size:14px;color:#64748b;font-weight:500;">Support Ticket System</div>
                    </td>
                </tr>
                <tr><td style="padding:32px 24px;color:#1e293b;">
                    <div style="background:linear-gradient(135deg, rgba(37,99,235,0.08), rgba(147,51,234,0.08));border-left:4px solid #2563eb;border-radius:8px;padding:16px 20px;margin-bottom:24px;">
                        <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#64748b;font-weight:700;margin-bottom:6px;">TICKET NUMBER</div>
                        <div style="font-size:24px;font-weight:800;color:#2563eb;letter-spacing:-0.5px;">${escapeHtml(TicketNumber)}</div>
                    </div>

                    ${subject ? `
                    <div style="background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;padding:20px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
                        <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#64748b;font-weight:700;margin-bottom:8px;">SUBJECT</div>
                        <div style="font-size:16px;font-weight:700;color:#1e293b;line-height:1.5;">${escapeHtml(subject)}</div>
                    </div>` : ""}

                    ${fromLine ? `
                    <div style="background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;padding:20px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
                        <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#64748b;font-weight:700;margin-bottom:8px;">FROM</div>
                        <div style="font-size:15px;color:#1e293b;line-height:1.5;">${fromLine}</div>
                    </div>` : ""}

                    ${priority ? `
                    <div style="background:#ffffff;border:1px solid #e2e8f0;border-radius:12px;padding:20px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
                        <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#64748b;font-weight:700;margin-bottom:8px;">PRIORITY</div>
                        <div style="display:inline-block;background:${priority.toLowerCase() === 'urgent' ? '#dc2626' : priority.toLowerCase() === 'high' ? '#f59e0b' : priority.toLowerCase() === 'low' ? '#10b981' : '#2563eb'};color:#ffffff;font-size:13px;font-weight:700;padding:6px 14px;border-radius:6px;text-transform:uppercase;letter-spacing:0.5px;">${escapeHtml(priority)}</div>
                    </div>` : ""}

                    ${body ? `
                    <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:20px;margin-bottom:24px;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
                        <div style="font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#64748b;font-weight:700;margin-bottom:12px;">MESSAGE</div>
                        <div style="font-size:15px;color:#334155;line-height:1.7;white-space:pre-wrap;font-family:'Segoe UI',Roboto,Arial,sans-serif;">${escapeHtml(body)}</div>
                    </div>` : ""}

                    ${extra || ""}

                    <div style="text-align:center;margin-top:32px;">
                        <a href="${SUPPORT_LINK}" style="display:inline-block;background:linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);color:#ffffff;text-decoration:none;font-weight:700;font-size:15px;padding:14px 32px;border-radius:12px;box-shadow:0 4px 12px rgba(37,99,235,0.3);transition:all 0.2s ease;">
                            ${escapeHtml(ctaLabel)}
                        </a>
                    </div>
                </td></tr>
                <tr>
                    <td align="center" style="padding:24px;background:#f8fafc;border-top:1px solid #e2e8f0;">
                        <div style="font-size:12px;color:#64748b;line-height:1.6;">
                            <strong>FIG Support</strong> • Automated notification<br>
                            <a href="${SUPPORT_LINK}" style="color:#2563eb;text-decoration:none;font-weight:600;">View Support Portal</a>
                        </div>
                    </td>
                </tr>
            </table>
        </td></tr>
    </table>
</body>
</html>`;
}

async function sendEmail({ to, subject, html, text }) {
    if (!to) return;
    await axios.post(MAILGUN_SEND, { to, subject, html, text });
}

export default function SupportPage() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [hideResolved, setHideResolved] = useState(true);
    const [confirm, setConfirm] = useState({ open: false, ticket: null });

    const commentsCache = useRef(new Map());
    const attachmentsCache = useRef(new Map());

    const [ntSubject, setNtSubject] = useState("");
    const [ntBody, setNtBody] = useState("");
    const [ntName, setNtName] = useState("");
    const [ntEmail, setNtEmail] = useState("");
    const [ntPhone, setNtPhone] = useState("");
    const [ntPriority, setNtPriority] = useState("Normal");
    const [ntFiles, setNtFiles] = useState([]);
    const [uploadingNew, setUploadingNew] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(API_TICKETS);
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setSnack({ open: true, msg: "Failed to load tickets.", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    const loadComments = useCallback(async (ticketId) => {
        if (commentsCache.current.has(ticketId)) return commentsCache.current.get(ticketId);
        const arr = await fetchByTicketId(API_COMMENTS, ticketId);
        const sorted = arr.sort((a, b) => new Date(a.CreatedOn) - new Date(b.CreatedOn));
        commentsCache.current.set(ticketId, sorted);
        return sorted;
    }, []);

    const loadAttachments = useCallback(async (ticketId) => {
        if (attachmentsCache.current.has(ticketId)) return attachmentsCache.current.get(ticketId);
        const arr = await fetchByTicketId(API_ATTACHMENTS, ticketId);
        const sorted = arr.sort((a, b) => new Date(a.CreatedOn) - new Date(b.CreatedOn));
        attachmentsCache.current.set(ticketId, sorted);
        return sorted;
    }, []);

    const invalidateComments = useCallback((ticketId) => commentsCache.current.delete(ticketId), []);
    const invalidateAttachments = useCallback((ticketId) => attachmentsCache.current.delete(ticketId), []);

    const uploadToMedia = async (file, friendlyName, onProgress) => {
        const form = new FormData();
        form.append("file", file, friendlyName);
        const { data } = await axios.post(`${MEDIA_BASE}/upload`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => { if (evt.total && onProgress) onProgress(Math.round((evt.loaded / evt.total) * 100)); },
        });
        return { fileUrl: data.fileUrl || data.url || "", contentType: data.contentType || file.type || null, size: data.size || file.size };
    };

    const markResolved = useCallback(async (row) => {
        try {
            setRows((rs) => rs.map((r) => (r.apiid === row.apiid ? { ...r, Status: "Resolved" } : r)));

            await axios.patch?.(`${API_TICKETS}/${row.apiid}`, { Status: "Resolved", UpdatedOn: new Date().toISOString() })
                .catch(async () => {
                    await axios.put(`${API_TICKETS}/${row.apiid}`, { ...row, Status: "Resolved", UpdatedOn: new Date().toISOString() });
                });

            const subject = `[Support] ${row.TicketNumber} resolved`;
            const html = buildEmailHTML({
                title: "Ticket Resolved",
                TicketNumber: row.TicketNumber,
                subject: row.Subject,
                body: "Your ticket has been marked as Resolved. If this is not correct, please reply or reopen in Support.",
                priority: row.Priority || "Normal",
            });
            const text = `Ticket ${row.TicketNumber} resolved\nSubject: ${row.Subject}\nPriority: ${row.Priority || "Normal"}`;

            await sendEmail({ to: "dusty@swank12.com", subject, html, text });
            if (row.RequesterEmail) {
                await sendEmail({ to: row.RequesterEmail, subject, html, text });
            }

            setSnack({ open: true, msg: `Ticket ${row.TicketNumber} marked Resolved.`, severity: "success" });
            vib(12);
        } catch (e) {
            console.error(e);
            setSnack({ open: true, msg: "Could not mark resolved.", severity: "error" });
            setRows((rs) => rs.map((r) => (r.apiid === row.apiid ? row : r)));
            vib([40, 40, 40]);
        }
    }, []);

    const deleteTicketDeep = useCallback(async (row) => {
        try {
            const atts = await loadAttachments(row.apiid);
            for (const a of atts) {
                try { await axios.delete(`${API_ATTACHMENTS}/${a.apiid}`); } catch { }
            }
            const cmts = await loadComments(row.apiid);
            for (const c of cmts) {
                try { await axios.delete(`${API_COMMENTS}/${c.apiid}`); } catch { }
            }
            await axios.delete(`${API_TICKETS}/${row.apiid}`);

            commentsCache.current.delete(row.apiid);
            attachmentsCache.current.delete(row.apiid);
            setRows((rs) => rs.filter((r) => r.apiid !== row.apiid));
            setSnack({ open: true, msg: `Deleted ${row.TicketNumber} and its comments/attachments.`, severity: "success" });
            vib(12);
        } catch (e) {
            console.error(e);
            setSnack({ open: true, msg: "Delete failed.", severity: "error" });
            vib([40, 40, 40]);
        }
    }, [loadAttachments, loadComments]);

    const handleCreateTicket = useCallback(async () => {
        const apiid = uuidv4();
        const TicketNumber = `SUP-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1e6)).padStart(6, "0")}`;
        const payload = {
            apiid,
            TicketNumber,
            Subject: ntSubject,
            Body: ntBody,
            RequesterName: ntName || null,
            RequesterEmail: ntEmail || null,
            RequesterPhone: ntPhone || null,
            Status: "Open",
            Priority: ntPriority || "Normal",
            Channel: "Web",
            CreatedOn: new Date().toISOString(),
        };

        try {
            await axios.post(API_TICKETS, payload);

            if (ntFiles.length) {
                setUploadingNew(true);
                setUploadProgress(0);
                let idx = 0;
                for (const f of ntFiles) {
                    const friendlyName = `${TicketNumber}-${sanitizeFilename(f.name)}`;
                    const mediaInfo = await uploadToMedia(f, friendlyName, (p) => {
                        const step = Math.floor((idx / ntFiles.length) * 100);
                        setUploadProgress(Math.min(99, step + Math.floor(p / ntFiles.length)));
                    });
                    const attPayload = {
                        apiid: uuidv4(),
                        TicketId: apiid,
                        CommentId: null,
                        FileName: friendlyName,
                        FileUrl: mediaInfo.fileUrl,
                        ContentType: mediaInfo.contentType,
                        FileSizeBytes: mediaInfo.size,
                        CreatedOn: new Date().toISOString(),
                    };
                    await axios.post(API_ATTACHMENTS, attPayload);
                    idx += 1;
                }
                setUploadProgress(100);
                setUploadingNew(false);
            }

            const staffHtml = buildEmailHTML({
                title: "New Support Ticket",
                TicketNumber,
                subject: ntSubject,
                body: ntBody,
                fromLine: `${ntName} &lt;${ntEmail}&gt; • ${ntPhone}`,
                priority: ntPriority,
            });
            const staffText = `New Support Ticket ${TicketNumber}\nPriority: ${ntPriority}\nSubject: ${ntSubject}\nFrom: ${ntName} <${ntEmail}>\nPhone: ${ntPhone}\n\n${ntBody}`;
            await sendEmail({
                to: "dusty@swank12.com",
                subject: `[Support] ${TicketNumber}: ${ntSubject} (${ntPriority})`,
                html: staffHtml,
                text: staffText,
            });

            if (ntEmail) {
                const userHtml = buildEmailHTML({
                    title: "Ticket Submitted",
                    TicketNumber,
                    subject: ntSubject,
                    body: ntBody,
                    fromLine: `${ntName} &lt;${ntEmail}&gt; • ${ntPhone}`,
                    priority: ntPriority,
                    ctaLabel: "View Your Ticket",
                });
                const userText = `Your ticket was submitted.\n${TicketNumber}\nPriority: ${ntPriority}\nSubject: ${ntSubject}\n\n${ntBody}`;
                await sendEmail({
                    to: ntEmail,
                    subject: `We received your ticket ${TicketNumber}`,
                    html: userHtml,
                    text: userText,
                });
            }

            vib(12);
            setSnack({ open: true, msg: `Submitted: ${TicketNumber}. Emails sent.`, severity: "success" });

            setNtSubject(""); setNtBody(""); setNtName(""); setNtEmail(""); setNtPhone("");
            setNtPriority("Normal"); setNtFiles([]); setUploadProgress(0);
            fetchTickets();
        } catch (e) {
            console.error(e);
            vib([40, 40, 40]);
            setSnack({ open: true, msg: "Could not submit ticket.", severity: "error" });
        }
    }, [ntSubject, ntBody, ntName, ntEmail, ntPhone, ntPriority, ntFiles, fetchTickets]);

    const priorityColor = useCallback((p) => {
        const v = (p || "").toString().toLowerCase();
        if (v === "urgent") return "error";
        if (v === "high") return "warning";
        if (v === "low") return "success";
        return "info";
    }, []);

    const columns = useMemo(() => {
        const textSx = { color: theme.palette.text.primary };
        return [
            {
                field: "TicketNumber",
                headerName: "Ticket",
                flex: 0.9,
                minWidth: 140,
                renderCell: (p) => (
                    <Typography sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{p.value}</Typography>
                ),
            },
            { field: "Subject", headerName: "Subject", flex: 1.3, minWidth: 220 },
            { field: "RequesterName", headerName: "Requester", flex: 0.9, minWidth: 140 },
            { field: "RequesterEmail", headerName: "Email", flex: 1, minWidth: 200 },
            {
                field: "Status",
                headerName: "Status",
                flex: 0.6,
                minWidth: 110,
                renderCell: (p) => (
                    <Chip
                        label={p.value || "Open"}
                        color={p.value?.toLowerCase() === "resolved" ? "success" : "default"}
                        size="small"
                        sx={{ fontWeight: 700 }}
                    />
                ),
            },
            {
                field: "Priority",
                headerName: "Priority",
                flex: 0.7,
                minWidth: 120,
                renderCell: (p) => (
                    <Chip
                        label={p.value || "Normal"}
                        color={priorityColor(p.value)}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            bgcolor: theme.palette.mode === "dark"
                                ? alpha(theme.palette[priorityColor(p.value)]?.main || theme.palette.info.main, 0.22)
                                : alpha(theme.palette[priorityColor(p.value)]?.light || theme.palette.info.light, 0.26),
                            border: `1px solid ${alpha(theme.palette[priorityColor(p.value)]?.main || theme.palette.info.main, 0.5)}`,
                            boxShadow: `0 0 8px ${alpha(theme.palette[priorityColor(p.value)]?.main || theme.palette.info.main, 0.3)}`,
                        }}
                    />
                ),
            },
            { field: "Category", headerName: "Category", flex: 0.7, minWidth: 120 },
            {
                field: "CreatedOn",
                headerName: "Created",
                valueGetter: (p) => (p.row.CreatedOn ? new Date(p.row.CreatedOn) : null),
                renderCell: (p) => <span style={textSx}>{p.value ? p.value.toLocaleString() : ""}</span>,
                flex: 0.8,
                minWidth: 160,
                sortComparator: (v1, v2) => (v1?.valueOf?.() || 0) - (v2?.valueOf?.() || 0),
            },
            {
                field: "actions",
                headerName: "Actions",
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                width: 120,
                renderCell: (p) => (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Mark Resolved" TransitionComponent={Zoom}>
                            <SolidIconButton className="resolve" size="small" onClick={() => markResolved(p.row)}>
                                <CheckCircleIcon fontSize="small" />
                            </SolidIconButton>
                        </Tooltip>
                        <Tooltip title="Delete Ticket" TransitionComponent={Zoom}>
                            <SolidIconButton className="delete" size="small" onClick={() => setConfirm({ open: true, ticket: p.row })}>
                                <DeleteForeverIcon fontSize="small" />
                            </SolidIconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ];
    }, [theme, priorityColor, markResolved]);

    const filteredRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return rows.filter((r) => {
            if (hideResolved && ["resolved", "closed"].includes((r.Status || "").toLowerCase())) return false;
            if (!q) return true;
            const hay = [
                r.TicketNumber, r.Subject, r.Body, r.RequesterName, r.RequesterEmail, r.RequesterPhone,
                r.Status, r.Priority, r.Category, r.TagsCsv,
            ].map((x) => (x || "").toString().toLowerCase()).join(" ");
            return hay.includes(q);
        });
    }, [rows, query, hideResolved]);

    const getDetailPanelContent = useCallback(
        ({ row }) => (
            <DetailPanel
                ticket={row}
                loadComments={loadComments}
                loadAttachments={loadAttachments}
                onDownload={async (att) => {
                    const suggested = `${sanitizeFilename(row.TicketNumber || "ticket")}-${sanitizeFilename(att.FileName)}`;
                    const url = att.FileUrl?.startsWith("http") ? att.FileUrl : `${MEDIA_BASE}/${att.FileUrl?.replace(/^\/+/, "")}`;
                    await downloadWithName(url, suggested);
                }}
                onCommentPosted={() => invalidateComments(row.apiid)}
                onAttachmentUploaded={() => invalidateAttachments(row.apiid)}
            />
        ),
        [loadComments, loadAttachments, invalidateComments, invalidateAttachments]
    );
    const getDetailPanelHeight = useCallback(() => "auto", []);

    return (
        <PageWrap>
            <Fade in timeout={500}>
                <Hero>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start" justifyContent="space-between">
                        <Stack spacing={1.5}>
                            <SectionTitle icon={<SupportAgentIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />}>
                                Customer Support
                            </SectionTitle>
                            <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 600 }}>
                                Create tickets, attach screenshots, discuss in comments. Open tickets are shown by default.
                            </Typography>
                            <FormControlLabel
                                control={<Switch checked={hideResolved} onChange={(e) => setHideResolved(e.target.checked)} color="secondary" />}
                                label={hideResolved ? "Open tickets only" : "Show all tickets"}
                                sx={{ mt: 1 }}
                            />
                        </Stack>
                        <SolidButton
                            variant="contained"
                            color="secondary"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={handleCreateTicket}
                            disabled={!ntSubject || !ntBody || uploadingNew}
                            size="large"
                        >
                            Submit Ticket
                        </SolidButton>
                    </Stack>

                    <Box sx={{ mt: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField label="Subject" fullWidth value={ntSubject} onChange={(e) => setNtSubject(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField label="Your Name" fullWidth value={ntName} onChange={(e) => setNtName(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField label="Phone" fullWidth value={ntPhone} onChange={(e) => setNtPhone(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField label="Email" fullWidth type="email" value={ntEmail} onChange={(e) => setNtEmail(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField label="Priority" select fullWidth value={ntPriority} onChange={(e) => setNtPriority(e.target.value)}>
                                    <MenuItem value="Low">Low</MenuItem>
                                    <MenuItem value="Normal">Normal</MenuItem>
                                    <MenuItem value="High">High</MenuItem>
                                    <MenuItem value="Urgent">Urgent</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Quick Search"
                                    fullWidth
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Describe the issue"
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    value={ntBody}
                                    onChange={(e) => setNtBody(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <SolidButton component="label" variant="contained" color="primary" startIcon={<AttachFileIcon />} disabled={uploadingNew}>
                                        Add Attachment(s)
                                        <input type="file" hidden multiple onChange={(e) => setNtFiles(Array.from(e.target.files || []))} />
                                    </SolidButton>
                                    {uploadingNew ? <LinearProgress variant="determinate" value={uploadProgress} sx={{ flex: 1 }} /> : null}
                                </Stack>
                                {ntFiles.length > 0 && (
                                    <List
                                        dense
                                        sx={{
                                            maxHeight: 140,
                                            overflow: "auto",
                                            border: `1px dashed ${alpha(theme.palette.text.primary, 0.25)}`,
                                            borderRadius: 2,
                                            p: 0,
                                            mt: 1,
                                        }}
                                    >
                                        {ntFiles.map((f) => {
                                            const friendly = `NEW-${sanitizeFilename(f.name)}`;
                                            return (
                                                <ListItem key={f.name} sx={{ py: 0.5 }}>
                                                    <ListItemText
                                                        primary={friendly}
                                                        secondary={`${(f.size / 1024).toFixed(1)} KB`}
                                                        primaryTypographyProps={{ noWrap: true, fontWeight: 700 }}
                                                        secondaryTypographyProps={{ noWrap: true }}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Hero>
            </Fade>

            <Fade in timeout={700}>
                <Card>
                    <Box
                        sx={{
                            height: "70vh",
                            "& .MuiDataGrid-root": {
                                border: "none",
                                backgroundColor: theme.palette.mode === "dark" ? colors.primary[600] : theme.palette.background.paper,
                                color: theme.palette.text.primary,
                            },
                            "& .MuiDataGrid-row:nth-of-type(even)": {
                                backgroundColor: theme.palette.mode === "dark" ? colors.primary[600] : theme.palette.background.paper,
                            },
                            "& .MuiDataGrid-row:nth-of-type(odd)": {
                                backgroundColor: theme.palette.mode === "dark"
                                    ? lighten(colors.primary[600], 0.28)
                                    : lighten(theme.palette.background.paper, 0.035),
                            },
                            "& .MuiDataGrid-cell": {
                                borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                background: theme.palette.mode === "dark"
                                    ? `linear-gradient(90deg, ${alpha(theme.palette.primary.dark, 0.5)}, ${alpha(theme.palette.secondary.dark, 0.4)})`
                                    : `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.35)}, ${alpha(theme.palette.secondary.light, 0.3)})`,
                                borderBottom: `2px solid ${alpha(theme.palette.divider, 0.6)}`,
                                fontWeight: 700,
                            },
                            "& .MuiDataGrid-toolbarContainer, & .MuiDataGrid-panel, & .MuiInputBase-root, & .MuiFormLabel-root, & .MuiDataGrid-filterForm": {
                                color: theme.palette.text.primary,
                            },
                            "& .MuiInputBase-input::placeholder": {
                                color: alpha(theme.palette.text.primary, 0.7),
                                opacity: 1,
                            },
                            "& .MuiDataGrid-row:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: theme.palette.mode === "dark"
                                    ? `0 8px 20px ${alpha(theme.palette.common.black, 0.5)}`
                                    : `0 8px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                                transition: "transform 150ms ease, box-shadow 150ms ease",
                            },
                        }}
                    >
                        <DataGridPro
                            loading={loading}
                            rows={filteredRows}
                            getRowId={(r) => r.apiid}
                            columns={columns}
                            disableRowSelectionOnClick
                            density="comfortable"
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{ toolbar: { showQuickFilter: true } }}
                            getDetailPanelContent={getDetailPanelContent}
                            getDetailPanelHeight={getDetailPanelHeight}
                            rowSelection={false}
                            pagination
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                        />
                    </Box>
                </Card>
            </Fade>

            <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, ticket: null })}>
                <DialogTitle>Delete Ticket?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently delete ticket <b>{confirm.ticket?.TicketNumber}</b> and all its comments and attachments.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm({ open: false, ticket: null })}>Cancel</Button>
                    <SolidButton
                        variant="contained"
                        color="error"
                        startIcon={<DeleteForeverIcon />}
                        onClick={async () => {
                            const t = confirm.ticket;
                            setConfirm({ open: false, ticket: null });
                            if (t) await deleteTicketDeep(t);
                        }}
                    >
                        Delete
                    </SolidButton>
                </DialogActions>
            </Dialog>

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
                <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </PageWrap>
    );
}

function DetailPanel({ ticket, loadComments, loadAttachments, onDownload, onCommentPosted, onAttachmentUploaded }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [comments, setComments] = useState(null);
    const [attachments, setAttachments] = useState(null);

    const [isPublic, setIsPublic] = useState(true);
    const [authorName, setAuthorName] = useState("");
    const [authorEmail, setAuthorEmail] = useState("");
    const [commentBody, setCommentBody] = useState("");
    const [posting, setPosting] = useState(false);
    const [localSnack, setLocalSnack] = useState({ open: false, msg: "", severity: "success" });

    const refreshDetails = useCallback(async () => {
        try {
            const [c, a] = await Promise.all([loadComments(ticket.apiid), loadAttachments(ticket.apiid)]);
            setComments(c);
            setAttachments(a);
        } catch (e) {
            console.error(e);
            setComments([]);
            setAttachments([]);
        }
    }, [ticket.apiid, loadComments, loadAttachments]);

    useEffect(() => { refreshDetails(); }, [refreshDetails]);

    const sendCommentEmails = useCallback(
        async ({ body, isPublicFlag, commentAuthorEmail }) => {
            const recipients = new Set();

            recipients.add("dusty@swank12.com");

            if (ticket.RequesterEmail) {
                recipients.add(ticket.RequesterEmail);
            }

            if (commentAuthorEmail && commentAuthorEmail !== ticket.RequesterEmail) {
                recipients.add(commentAuthorEmail);
            }

            for (const email of recipients) {
                const isStaff = email === "dusty@swank12.com";

                if (!isPublicFlag && !isStaff) continue;

                const emailHtml = buildEmailHTML({
                    title: isStaff ? "New Ticket Comment" : "Update on Your Ticket",
                    TicketNumber: ticket.TicketNumber,
                    subject: ticket.Subject,
                    body,
                    fromLine: authorName ? `${authorName}${authorEmail ? " &lt;" + authorEmail + "&gt;" : ""}` : "",
                    priority: ticket.Priority || "Normal",
                    ctaLabel: isStaff ? "Open Support" : "View Your Ticket",
                });

                const emailText = `${isStaff ? "New comment" : "Update"} on ${ticket.TicketNumber}\nSubject: ${ticket.Subject}\n\n${body}`;

                await sendEmail({
                    to: email,
                    subject: isStaff ? `[Support] Comment on ${ticket.TicketNumber}` : `Update on ${ticket.TicketNumber}`,
                    html: emailHtml,
                    text: emailText,
                });
            }
        },
        [ticket.TicketNumber, ticket.Subject, ticket.Priority, ticket.RequesterEmail, authorName, authorEmail]
    );

    const handlePostComment = useCallback(async () => {
        if (!commentBody.trim()) {
            setLocalSnack({ open: true, msg: "Please enter a comment.", severity: "warning" });
            vib([30, 40, 30]);
            return;
        }
        setPosting(true);
        try {
            const payload = {
                apiid: uuidv4(),
                TicketId: ticket.apiid,
                IsPublic: isPublic ? 1 : 0,
                AuthorName: authorName || (isPublic ? "Requester" : "Internal"),
                AuthorEmail: authorEmail || null,
                Body: commentBody,
                CreatedOn: new Date().toISOString(),
            };
            await axios.post(API_COMMENTS, payload);

            onCommentPosted?.(ticket.apiid);
            await refreshDetails();

            await sendCommentEmails({
                body: payload.Body,
                isPublicFlag: !!payload.IsPublic,
                commentAuthorEmail: payload.AuthorEmail
            });

            setCommentBody("");
            setIsPublic(true);
            setLocalSnack({ open: true, msg: "Comment posted and emails sent.", severity: "success" });
            vib(12);
        } catch (e) {
            console.error(e);
            setLocalSnack({ open: true, msg: "Failed to post comment.", severity: "error" });
            vib([40, 40, 40]);
        } finally {
            setPosting(false);
        }
    }, [ticket.apiid, isPublic, authorName, authorEmail, commentBody, onCommentPosted, refreshDetails, sendCommentEmails]);

    return (
        <Box sx={{ p: 3, background: alpha(theme.palette.background.paper, 0.8), borderRadius: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{
                        background: theme.palette.mode === "dark"
                            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.3)}, ${alpha(theme.palette.secondary.dark, 0.2)})`
                            : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(theme.palette.secondary.light, 0.1)})`,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}>
                        <Stack spacing={2}>
                            <Typography variant="h4" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5 }}>
                                <DescriptionIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                                Original Ticket
                            </Typography>

                            <Box sx={{
                                p: 2,
                                borderRadius: 2,
                                background: theme.palette.mode === "dark"
                                    ? alpha(theme.palette.background.paper, 0.5)
                                    : alpha(theme.palette.background.paper, 0.8),
                                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                            }}>
                                <Typography variant="caption" sx={{
                                    textTransform: "uppercase",
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    opacity: 0.7,
                                    display: "block",
                                    mb: 0.5,
                                }}>
                                    Subject
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                    {ticket.Subject || "No subject"}
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="caption" sx={{
                                    textTransform: "uppercase",
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    opacity: 0.7,
                                    display: "block",
                                    mb: 1,
                                }}>
                                    Description
                                </Typography>
                                <Typography variant="body1" sx={{
                                    whiteSpace: "pre-wrap",
                                    lineHeight: 1.7,
                                    p: 2,
                                    borderRadius: 2,
                                    background: theme.palette.mode === "dark"
                                        ? alpha(colors.primary[700], 0.4)
                                        : alpha(colors.primary[900], 0.02),
                                }}>
                                    {ticket.Body || "No description provided"}
                                </Typography>
                            </Box>

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip
                                    label={`Submitted: ${ticket.CreatedOn ? new Date(ticket.CreatedOn).toLocaleString() : 'N/A'}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                                {ticket.RequesterName && (
                                    <Chip
                                        label={`By: ${ticket.RequesterName}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                )}
                                {ticket.RequesterEmail && (
                                    <Chip
                                        label={ticket.RequesterEmail}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                )}
                                {ticket.RequesterPhone && (
                                    <Chip
                                        label={ticket.RequesterPhone}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5 }}>
                        <CommentIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                        Conversation
                    </Typography>

                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                            background: theme.palette.mode === "dark" ? alpha(colors.primary[700], 0.6) : alpha(colors.primary[900], 0.04),
                            maxHeight: 400,
                            overflow: "auto",
                        }}
                    >
                        {comments === null ? (
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Loading comments…</Typography>
                        ) : comments.length === 0 ? (
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>No comments yet.</Typography>
                        ) : (
                            <Stack spacing={2}>
                                {comments.map((c) => (
                                    <Zoom in key={c.apiid}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                background: c.IsPublic
                                                    ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.12)}, ${alpha(theme.palette.success.light, 0.08)})`
                                                    : `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.12)}, ${alpha(theme.palette.warning.light, 0.08)})`,
                                                border: `1px solid ${alpha(c.IsPublic ? theme.palette.success.main : theme.palette.warning.main, 0.3)}`,
                                                transition: "transform 150ms ease, box-shadow 150ms ease",
                                                "&:hover": {
                                                    transform: "translateY(-2px)",
                                                    boxShadow: `0 4px 12px ${alpha(c.IsPublic ? theme.palette.success.main : theme.palette.warning.main, 0.2)}`,
                                                },
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                    {c.AuthorName || (c.IsPublic ? "Requester" : "Internal")}
                                                    {c.AuthorEmail ? (
                                                        <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
                                                            &lt;{c.AuthorEmail}&gt;
                                                        </Typography>
                                                    ) : null}
                                                </Typography>
                                                <Chip
                                                    label={c.IsPublic ? "Public" : "Internal"}
                                                    size="small"
                                                    color={c.IsPublic ? "success" : "warning"}
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            </Stack>
                                            <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 1 }}>
                                                {c.CreatedOn ? new Date(c.CreatedOn).toLocaleString() : ""}
                                            </Typography>
                                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{c.Body}</Typography>
                                        </Box>
                                    </Zoom>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Card sx={{ mt: 3 }}>
                        <Stack spacing={2}>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Add Comment</Typography>
                            <FormControlLabel
                                control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} color="secondary" />}
                                label={
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {isPublic ? "Public (visible to all parties)" : "Internal note (staff only)"}
                                    </Typography>
                                }
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField label="Your Name" fullWidth value={authorName} onChange={(e) => setAuthorName(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField label="Your Email" fullWidth type="email" value={authorEmail} onChange={(e) => setAuthorEmail(e.target.value)} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label={isPublic ? "Write a public reply" : "Write an internal note"}
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        value={commentBody}
                                        onChange={(e) => setCommentBody(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <SolidButton
                                        variant="contained"
                                        color="secondary"
                                        startIcon={posting ? null : <SendIcon />}
                                        onClick={handlePostComment}
                                        disabled={posting || !commentBody.trim()}
                                        size="large"
                                    >
                                        {posting ? <CircularProgress size={20} /> : "Post Comment & Notify All"}
                                    </SolidButton>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5 }}>
                        <AttachFileIcon sx={{ fontSize: 32, color: theme.palette.secondary.main }} />
                        Attachments
                    </Typography>

                    <AttachmentUploader
                        ticket={ticket}
                        onUploaded={async () => {
                            onAttachmentUploaded?.(ticket.apiid);
                            try {
                                const [c, a] = await Promise.all([loadComments(ticket.apiid), loadAttachments(ticket.apiid)]);
                                setComments(c);
                                setAttachments(a);
                            } catch (e) { console.error(e); }
                        }}
                    />

                    <Box
                        sx={{
                            p: 2,
                            mt: 2,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                            background: theme.palette.mode === "dark" ? alpha(colors.primary[700], 0.6) : alpha(colors.primary[900], 0.04),
                            maxHeight: 400,
                            overflow: "auto",
                        }}
                    >
                        {attachments === null ? (
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Loading attachments…</Typography>
                        ) : attachments.length === 0 ? (
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>No attachments.</Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {attachments.map((a) => {
                                    const label = a.FileName || "file";
                                    return (
                                        <Zoom in key={a.apiid}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    background: alpha(theme.palette.info.main, 0.1),
                                                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                                    transition: "all 150ms ease",
                                                    "&:hover": {
                                                        transform: "translateY(-2px)",
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.2)}`,
                                                    },
                                                }}
                                            >
                                                <AttachmentPill icon={<AttachFileIcon />} label={label} variant="outlined" />
                                                <Box sx={{ flexGrow: 1 }} />
                                                <Tooltip title="Download" TransitionComponent={Zoom}>
                                                    <IconButton onClick={() => onDownload(a)} size="small" color="secondary">
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Zoom>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={ticket.Status || "Open"} size="small" color="success" sx={{ fontWeight: 700 }} />
                <Chip label={ticket.Priority || "Normal"} size="small" color="primary" sx={{ fontWeight: 700 }} />
                {ticket.Category ? <Chip label={ticket.Category} size="small" variant="outlined" sx={{ fontWeight: 700 }} /> : null}
                {ticket.TagsCsv ? ticket.TagsCsv.split(/[;,]+/).slice(0, 6).map((t) => <Chip key={t} label={t.trim()} size="small" variant="outlined" sx={{ fontWeight: 700 }} />) : null}
            </Stack>

            <Snackbar open={localSnack.open} autoHideDuration={3000} onClose={() => setLocalSnack((s) => ({ ...s, open: false }))}>
                <Alert severity={localSnack.severity} onClose={() => setLocalSnack((s) => ({ ...s, open: false }))}>
                    {localSnack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}

function AttachmentUploader({ ticket, onUploaded }) {
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const [msg, setMsg] = useState("");

    const uploadToMedia = async (file, friendlyName) => {
        const form = new FormData();
        form.append("file", file, friendlyName);
        const { data } = await axios.post(`${MEDIA_BASE}/upload`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
                if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
            },
        });
        return {
            fileUrl: data.fileUrl || data.url || "",
            contentType: data.contentType || file.type || null,
            size: data.size || file.size,
        };
    };

    const handleUpload = async () => {
        if (!files.length) return;
        setBusy(true);
        setMsg("");

        try {
            for (const f of files) {
                const friendlyName = `${ticket.TicketNumber || "ticket"}-${sanitizeFilename(f.name)}`;
                const mediaInfo = await uploadToMedia(f, friendlyName);

                const payload = {
                    apiid: uuidv4(),
                    TicketId: ticket.apiid,
                    CommentId: null,
                    FileName: friendlyName,
                    FileUrl: mediaInfo.fileUrl,
                    ContentType: mediaInfo.contentType,
                    FileSizeBytes: mediaInfo.size,
                    CreatedOn: new Date().toISOString(),
                };
                await axios.post(API_ATTACHMENTS, payload);
            }

            vib(12);
            setMsg("Upload complete.");
            setFiles([]);
            setProgress(0);
            onUploaded?.();
        } catch (e) {
            console.error(e);
            vib([40, 40, 40]);
            setMsg("Upload failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card>
            <Stack spacing={2}>
                <Typography variant="h5" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
                    <CloudUploadIcon /> Upload attachments
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Files will be saved with a friendly name and linked to this ticket.
                </Typography>

                <Stack direction="row" alignItems="center" spacing={1}>
                    <SolidButton component="label" variant="contained" color="primary" startIcon={<AttachFileIcon />} disabled={busy}>
                        Choose Files
                        <input type="file" hidden multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                    </SolidButton>
                    <SolidButton
                        variant="contained"
                        color="secondary"
                        startIcon={busy ? null : <CloudUploadIcon />}
                        onClick={handleUpload}
                        disabled={busy || files.length === 0}
                    >
                        {busy ? <CircularProgress size={20} /> : "Upload"}
                    </SolidButton>
                </Stack>

                {busy ? <LinearProgress variant="determinate" value={progress} /> : null}

                {files.length > 0 && (
                    <List dense sx={{ maxHeight: 140, overflow: "auto", border: `1px dashed rgba(0,0,0,0.2)`, borderRadius: 2, p: 0 }}>
                        {files.map((f) => {
                            const friendly = `${ticket.TicketNumber || "ticket"}-${sanitizeFilename(f.name)}`;
                            return (
                                <ListItem key={f.name} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary={friendly}
                                        secondary={`${(f.size / 1024).toFixed(1)} KB`}
                                        primaryTypographyProps={{ noWrap: true, fontWeight: 700 }}
                                        secondaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}

                {msg ? <Alert severity={msg.includes("failed") ? "error" : "success"}>{msg}</Alert> : null}
            </Stack>
        </Card>
    );
}